import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, UIMessage } from "ai";
import Cookies from "js-cookie";
import {
    ChangeEvent,
    createContext,
    FormEvent,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useSelector } from "react-redux";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { getRuntimeEnv } from "vite-runtime-env-script-plugin/getRuntimeEnv";

import { constantsData } from "@/data";
import { AlfredChatContextInterface, ApprovalRequest, CurrentUserInterface } from "@/interfaces";
import { axios } from "@/lib/axios";
import { getErrorMessageHandler, normalizeAuthHandler } from "@/lib/utils";
import { toast } from "@/ui/use-toast";

const convertToSimpleMessagesHandler = (messages: UIMessage[]): Array<{ content: string, role: string; }> => messages.map((msg) => {
    let content = "";

    if (msg.parts && msg.parts.length > 0) content = msg.parts.filter((part): part is { text: string, type: "text"; } => part.type === "text").map((part) => part.text).join("");

    return {
        content,
        role: msg.role,
    };
});

const AlfredChatContext = createContext<AlfredChatContextInterface | null>(null);

/**
 * SECURITY: Allowlist of permitted pageContext keys.
 * Only these keys can be passed from page components to the backend.
 * This prevents accidental leakage of sensitive data via pageContext.
 */
const ALLOWED_PAGE_CONTEXT_KEYS = [
    "currentAccountId",
    "currentDataSourceId",
    "currentReconciliationId",
    "currentTransactionId",
    "viewType",
    "filterState",
    "dateRange",
] as const;

export const useAlfredChatContext = () => {
    const context = useContext(AlfredChatContext);

    if (!context) throw new Error(constantsData.msgs.providers.alfredChat);

    return context;
};

export const AlfredChatProvider = ({ children }: { children: ReactNode }) => {
    const { pathname } = useLocation();
    const params = useParams();
    const [searchParams] = useSearchParams();

    const rootReducer = useSelector((state) => state as { user: CurrentUserInterface });

    const { user } = rootReducer;

    const {
        id,
        isAuthenticated,
    } = user;

    const [isOpen, setIsOpen] = useState(false);

    const [pageContext, setPageContext] = useState<Record<string, unknown>>({});

    const [input, setInput] = useState("");

    const [pendingApprovals, setPendingApprovals] = useState<ApprovalRequest[]>([]);

    const updatePageContext = useCallback(
        (context: Record<string, unknown>) => setPageContext(context),
        [],
    );

    const token = useMemo(
        () => (isAuthenticated ? normalizeAuthHandler(Cookies.get("accessToken") as string) : ""),
        [isAuthenticated],
    );

    const threadId = useMemo(
        () => `${id}:${pathname?.replace(
            /^\//,
            "",
        )}`,
        [id, pathname],
    );

    /**
     * SECURITY: Generate CSRF token for state-changing operations.
     * Uses crypto.randomUUID() for cryptographically secure token generation.
     * Token is stored in session and validated on each request.
     */
    const csrfToken = useMemo(
        () => {
            // Check if we already have a CSRF token in sessionStorage
            let storedToken = sessionStorage.getItem("alfred_csrf_token");
            if (!storedToken) {
                storedToken = crypto.randomUUID();
                sessionStorage.setItem(
                    "alfred_csrf_token",
                    storedToken,
                );
            }
            return storedToken;
        },
        [],
    );

    // Convert searchParams to plain object
    const queryObject = useMemo(
        () => {
            const obj: Record<string, string> = {};
            searchParams.forEach((value, key) => {
                obj[key] = value;
            });
            return obj;
        },
        [searchParams],
    );

    // Sanitize pageContext to only include allowed keys
    const sanitizedPageContext = useMemo(
        () => {
            const sanitized: Record<string, unknown> = {};
            ALLOWED_PAGE_CONTEXT_KEYS.forEach((key) => {
                if (key in pageContext && pageContext[key] !== undefined) sanitized[key] = pageContext[key];
            });

            return sanitized;
        },
        [pageContext],
    );

    /* Construct UISnapshot with routeKey, params, and query
       SECURITY: Authorization token is ONLY sent in headers, never in body */
    const uiSnapshot = useMemo(
        () => ({
            params,
            query: queryObject,
        routeKey: pathname?.replace(/^\//, "") || "dashboard", // eslint-disable-line
        thread_id: threadId, // eslint-disable-line
        user_id: id, // eslint-disable-line
            ...sanitizedPageContext,
        }),
        [params, queryObject, pathname, threadId, id, sanitizedPageContext],
    );

    const alfredAgentUrl = useMemo(
        () => getRuntimeEnv("ALFRED_AGENT_URL"),
        [],
    );

    const transport = useMemo(
        () => new DefaultChatTransport({
            api: `${alfredAgentUrl}/api/chat`,
            credentials: "include",
            headers: { Authorization: token || "" },
            prepareSendMessagesRequest: ({
                messages: chatMessages,
            }) => ({
                body: {
                    data: uiSnapshot,
                    messages: convertToSimpleMessagesHandler(chatMessages),
                },
            }),
        }),
        [token, uiSnapshot, alfredAgentUrl],
    );

    /* Extract approval requests from message parts
       Supports both SSE tool-output-available (toolName: approval_required) and tool-result formats */
    const extractApprovalsFromMessages = useCallback(
        (messages: UIMessage[]): ApprovalRequest[] => {
            const approvals: ApprovalRequest[] = [];

            messages.forEach((message) => {
                if (message.role === "assistant" && message.parts) {
                    message.parts.forEach((part) => {
                        const typedPart = part as {
                            output?: unknown;
                            result?: unknown;
                            toolName?: string;
                            type: string;
                        };

                        /* Check for approval request in tool outputs
                           Handle multiple toolName variants: approval_required (new SSE), request_approval (legacy) */
                        const isApprovalTool = typedPart.toolName === "approval_required"
                            || typedPart.toolName === "approval_request"
                            || typedPart.toolName === "request_approval";

                        if (isApprovalTool) {
                            // Check output (SSE tool-output-available) or result (tool-result)
                            const output = typedPart.output ?? typedPart.result;
                            if (output && typeof output === "object" && "approval_id" in output) {
                                // Convert minimal SSE payload to full ApprovalRequest with defaults
                                const rawApproval = output as Record<string, unknown>;
                                /* eslint-disable camelcase */
                                const approval: ApprovalRequest = {
                                    approval_id: rawApproval.approval_id as string,
                                    approvals: (rawApproval.approvals as ApprovalRequest["approvals"]) || [],
                                    created_at: (rawApproval.created_at as string) || new Date().toISOString(),
                                    expires_at: (rawApproval.expires_at as string) || new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
                                    mfa_verified: (rawApproval.mfa_verified as boolean) ?? false,
                                    operation: (rawApproval.operation as string) || "unknown",
                                    organization_id: (rawApproval.organization_id as string) || "",
                                    parameters: (rawApproval.parameters as Record<string, unknown>) || {},
                                    payload_hash: (rawApproval.payload_hash as string) || "",
                                    quorum_count: (rawApproval.quorum_count as number) ?? 1,
                                    requestor: (rawApproval.requestor as string) || ((rawApproval.requestor as Record<string, unknown>)?.user_id as string) || "",
                                    requestor_name: rawApproval.requestor_name as string | undefined,
                                    requires_quorum: (rawApproval.requires_quorum as boolean) ?? false,
                                    status: (rawApproval.status as ApprovalRequest["status"]) || "pending",
                                };
                                /* eslint-enable camelcase */
                                approvals.push(approval);
                            }
                        }
                    });
                }
            });

            return approvals;
        },
        [],
    );

    // Refresh approvals from backend - uses /api/alfred/approvals endpoint
    const refreshApprovals = useCallback(
        async () => {
            try {
                const response = await axios.get(
                    `${alfredAgentUrl}/api/alfred/approvals`,
                    {
                        params: { thread_id: threadId }, // eslint-disable-line camelcase
                    },
                );
                setPendingApprovals(response.data.approvals || []);
            } catch (error) {
                console.error( // eslint-disable-line no-console
                    "Failed to refresh approvals:",
                    error,
                );
                toast({
                    description: getErrorMessageHandler(
                        error,
                        "Failed to refresh approvals",
                    ),
                    title: "Error",
                    variant: "danger",
                });
            }
        },
        [alfredAgentUrl, threadId],
    );

    // Track in-flight approval requests to prevent duplicates
    const [processingApprovals, setProcessingApprovals] = useState<Set<string>>(new Set());

    /* Handle approval - uses /api/alfred/approve endpoint
       SECURITY: Includes CSRF token and idempotency key to prevent CSRF and duplicate submissions */
    const handleApprove = useCallback(
        async (approvalId: string, mfaToken?: string) => {
            // SECURITY: Prevent duplicate submissions via race condition
            if (processingApprovals.has(approvalId)) {
                console.warn(`Approval ${approvalId} is already being processed`); // eslint-disable-line no-console
                return;
            }

            // Mark as processing immediately
            setProcessingApprovals((prev) => new Set(prev).add(approvalId));

            // SECURITY: Generate idempotency key for this specific approval action
            const idempotencyKey = crypto.randomUUID();

            try {
                await axios.post(
                    `${alfredAgentUrl}/api/alfred/approve`,
                    /* eslint-disable camelcase */
                    {
                        approval_id: approvalId,
                        idempotency_key: idempotencyKey,
                        mfa_token: mfaToken,
                    },
                    /* eslint-enable camelcase */
                    {
                        headers: {
                            "X-CSRF-Token": csrfToken,
                            "X-Idempotency-Key": idempotencyKey,
                        },
                    },
                );

                // Remove from pending approvals
                setPendingApprovals((prev) => prev.filter((approval) => approval.approval_id !== approvalId));

                toast({
                    description: "The operation has been approved successfully",
                    title: "Approval Submitted",
                    variant: "default",
                });

                // Optionally refresh approvals to get updated state
                await refreshApprovals();
            } catch (error) {
                console.error( // eslint-disable-line no-console
                    "Failed to approve:",
                    error,
                );
                toast({
                    description: getErrorMessageHandler(
                        error,
                        "Failed to approve the request",
                    ),
                    title: "Approval Failed",
                    variant: "danger",
                });
                throw error;
            } finally {
                // SECURITY: Always remove from processing set to allow retry on failure
                setProcessingApprovals((prev) => {
                    const next = new Set(prev);
                    next["delete"](approvalId);
                    return next;
                });
            }
        },
        [alfredAgentUrl, refreshApprovals, csrfToken, processingApprovals],
    );

    /* Handle rejection - uses /api/alfred/reject endpoint
       SECURITY: Includes CSRF token and idempotency key */
    const handleReject = useCallback(
        async (approvalId: string, reason?: string) => {
            // SECURITY: Prevent duplicate submissions
            if (processingApprovals.has(approvalId)) {
                console.warn(`Rejection ${approvalId} is already being processed`); // eslint-disable-line no-console
                return;
            }

            setProcessingApprovals((prev) => new Set(prev).add(approvalId));
            const idempotencyKey = crypto.randomUUID();

            try {
                await axios.post(
                    `${alfredAgentUrl}/api/alfred/reject`,
                    /* eslint-disable camelcase */
                    {
                        approval_id: approvalId,
                        idempotency_key: idempotencyKey,
                        reason,
                    },
                    /* eslint-enable camelcase */
                    {
                        headers: {
                            "X-CSRF-Token": csrfToken,
                            "X-Idempotency-Key": idempotencyKey,
                        },
                    },
                );

                // Remove from pending approvals
                setPendingApprovals((prev) => prev.filter((approval) => approval.approval_id !== approvalId));

                toast({
                    description: "The request has been rejected",
                    title: "Request Rejected",
                    variant: "default",
                });

                // Optionally refresh approvals to get updated state
                await refreshApprovals();
            } catch (error) {
                console.error( // eslint-disable-line no-console
                    "Failed to reject:",
                    error,
                );
                toast({
                    description: getErrorMessageHandler(
                        error,
                        "Failed to reject the request",
                    ),
                    title: "Rejection Failed",
                    variant: "danger",
                });
                throw error;
            } finally {
                // SECURITY: Always remove from processing set to allow retry on failure
                setProcessingApprovals((prev) => {
                    const next = new Set(prev);
                    next["delete"](approvalId);
                    return next;
                });
            }
        },
        [alfredAgentUrl, refreshApprovals, csrfToken, processingApprovals],
    );

    const chat = useChat({
        id: threadId,
        onError: (error: Error) => {
            console.error( // eslint-disable-line no-console
                "Alfred chat error:",
                error,
            );
        },
        transport,
    });

    // Extract approvals from messages whenever messages change
    useEffect(
        () => {
            const approvals = extractApprovalsFromMessages(chat.messages);
            if (approvals.length > 0) {
                setPendingApprovals((prev) => {
                    // Merge new approvals with existing ones, avoiding duplicates
                    const existingIds = new Set(prev.map((a) => a.approval_id));
                    const newApprovals = approvals.filter((a) => !existingIds.has(a.approval_id));
                    return [...prev, ...newApprovals];
                });
            }
        },
        [chat.messages, extractApprovalsFromMessages],
    );

    const handleInputChange = useCallback(
        (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setInput(e.target.value);
        },
        [],
    );

    const sendMessage = useCallback(
        (message: string) => {
            if (!message.trim()) return;
            chat.sendMessage({ text: message });
            setInput("");
        },
        [chat],
    );

    const handleSubmit = useCallback(
        (e?: FormEvent) => {
            e?.preventDefault?.();
            sendMessage(input);
        },
        [sendMessage, input],
    );

    const isLoading = chat.status === "streaming" || chat.status === "submitted";

    // SECURITY: Helper to check if a specific approval is being processed
    const isApprovalProcessing = useCallback(
        (approvalId: string) => processingApprovals.has(approvalId),
        [processingApprovals],
    );

    const newConversation = useCallback(
        () => {
            // Clear existing messages and start fresh
            chat.setMessages([]);
        },
        [chat],
    );

    const resumeConversation = useCallback(
        (newThreadId: string) => {
            /* This would normally load conversation history from the backend
               For now, we just clear messages since MemorySaver doesn't persist across sessions */
            console.log(`Resuming conversation: ${newThreadId}`); // eslint-disable-line no-console
            chat.setMessages([]);
        },
        [chat],
    );

    const contextValue = useMemo<AlfredChatContextInterface>(
        () => ({
            error: chat.error,
            handleApprove,
            handleInputChange,
            handleReject,
            handleSubmit,
            input,
            isApprovalProcessing,
            isLoading,
            isOpen,
            messages: chat.messages,
            newConversation,
            pageContext,
            pendingApprovals,
            refreshApprovals,
            regenerate: chat.regenerate,
            resumeConversation,
            sendMessage,
            setInput,
            setIsOpen,
            stop: chat.stop,
            threadId,
            updatePageContext,
        }),
        [
            chat.messages,
            chat.error,
            chat.regenerate,
            chat.stop,
            handleApprove,
            handleReject,
            input,
            handleSubmit,
            handleInputChange,
            isApprovalProcessing,
            isLoading,
            pendingApprovals,
            refreshApprovals,
            sendMessage,
            isOpen,
            newConversation,
            pageContext,
            resumeConversation,
            threadId,
            updatePageContext,
        ],
    );

    return (
        <AlfredChatContext.Provider value={contextValue}>
            {children}
        </AlfredChatContext.Provider>
    );
};
